import { ZapparCamera } from '@zappar/zappar-react-three-fiber/lib/index-standalone';
import React, { useCallback, useEffect, useMemo, useRef, useState, FunctionComponent } from 'react';
import { debugLog } from '../debug-log';
import { Jute } from '..';
import { useCameraIsUserFacing } from '../hooks/general';
import { Camera } from '@zappar/zappar-threejs';
import { camera_profile_t } from '@zappar/zappar-cv';
import { HTMLElementSource as CvHtmlElementSource } from '@zappar/zappar-cv/lib/html-element-source';
import { useGetActiveTrackingType } from './hoc/hocCustomHooks';
import { ITrackingTypes } from '@r3f-component-data-structure';

export let zapparCameraInstance: Camera | undefined;

/**
 * Ask the CV pipeline for its HIGH profile when the frames come from a host source.
 *
 * The tracker resamples every frame into a fixed buffer — 320x180 by default, 640x360 on HIGH — so
 * this is four times the pixels to find features in. It matters far more for a synthetic camera than
 * a real one: the source is landscape while the preview window is portrait, so most of the frame is
 * cropped away on screen and the target lands on relatively few of the tracked rows.
 *
 * The intrinsics stay angularly identical (focal length and principal point both scale with the
 * buffer), so nothing about pose or FOV changes — only the resolution the detector works at.
 *
 * Done by hand because `Camera.profile` only forwards to a real `CameraSource`; it ignores an
 * `HTMLElementSource` entirely. That means reaching past the wrapper to the zappar-cv instance, so it
 * is wrapped in a try/catch — if a library update moves this, tracking quietly falls back to DEFAULT
 * rather than the camera failing outright.
 */
function requestHighProfile(camera: Camera): void {
	try {
		for (const source of [camera.rearCameraSource, camera.userCameraSource]) {
			const impl = (source as unknown as { _impl?: number })?._impl;
			if (typeof impl !== 'number') continue;
			CvHtmlElementSource.getVideoElementSource(impl as never)?.setProfile(camera_profile_t.HIGH);
		}
		console.log('jute manager: camera source set to the HIGH profile (640x360 tracking buffer)');
	} catch (err) {
		console.warn('jute manager: could not select the HIGH camera profile, tracking stays at 320x180', err);
	}
}

const ZapparCameraWrapper: FunctionComponent = () => {
	const isUserFacing = useCameraIsUserFacing();
	const firstFrameHasRun = useRef(false);
	const activeTrackingType = useGetActiveTrackingType()
	const [cameraSource, setCameraSource] = useState(() => Jute.cameraSource);

	// Re-read on subscribe, not just in the initial state. The host sets the source from another
	// document on this same thread, so it can land between this component's render and this effect —
	// and onCameraSourceChange only replays to callbacks registered before the call. Dropping it there
	// left the camera unstarted for the life of the page, on a launch screen already consumed.
	useEffect(() => {
		setCameraSource(Jute.cameraSource);
		return Jute.onCameraSourceChange(() => setCameraSource(Jute.cameraSource));
	}, []);

	// ZapparCamera rebuilds its ZapparThree.Camera whenever `sources` changes identity, so this must not
	// be an inline literal. BOTH slots: the Camera constructor builds a real CameraSource for whichever
	// one is left undefined, and starting that would prompt for the device camera.
	//
	// The cast is for a canvas — the library's `Source` type lists only video and image, but the runtime
	// check is `instanceof Element` and texImage2D takes a canvas happily.
	const sources = useMemo(
		() =>
			cameraSource
				? { rearCamera: cameraSource as HTMLVideoElement, userCamera: cameraSource as HTMLVideoElement }
				: undefined,
		[cameraSource]
	);

	// ensure that on first frame is fired off only once (it has fired off more than once in the past)
	const onFirstFrame = useCallback(() => {
		if (firstFrameHasRun.current) return;
		debugLog('ZapparCamera: first frame from the camera');
		Jute.firstCameraFrame?.();
		firstFrameHasRun.current = true;
	}, []);

	// What the camera was actually handed. `start` false means it never opens; `permissionRequest` true
	// means Zappar shows its own consent UI before touching getUserMedia.
	useEffect(() => {
		debugLog('ZapparCamera mounted', {
			hasCameraSource: !!cameraSource,
			start: !!cameraSource || !Jute.isCameraSourcePending,
			permissionRequest: !cameraSource,
			isCameraSourcePending: Jute.isCameraSourcePending,
			userFacing: isUserFacing,
			activeTrackingType
		});
	}, [cameraSource, isUserFacing, activeTrackingType]);

	// A host source always reports its frames as rear-facing, so the library would apply
	// rearCameraMirrorMode ('none') where a real selfie camera gets userCameraMirrorMode ('poses'). That
	// matters because ArComponents maps face landmarks with the front-camera swap hardcoded on
	// (`uarTarget(landmark, true)`), so unmirrored poses put left-eye content on the right eye. Mirroring
	// exactly when we would have asked for the user camera reproduces the real behaviour. Image targets
	// must NOT be mirrored.
	const rearCameraMirrorMode = cameraSource && isUserFacing ? 'poses' : 'none';

	return (
		// @ts-ignore
		<ZapparCamera
			ref={self => {
				if (!self) return;
				(self as Camera).name = 'zappar-camera';
				zapparCameraInstance = self;
				// Here rather than in an effect: the sources are built in the Camera's constructor, so
				// this is the earliest point they exist — and it re-runs whenever `sources` changes
				// identity and the Camera is rebuilt.
				if (cameraSource) requestHighProfile(self as Camera);
			}}
			dispose={null}
			renderPriority={-1}
			environmentMap={activeTrackingType !== ITrackingTypes.content360}
			/**
			 * Give up the render camera while the desktop preview shows a world-tracked scene.
			 *
			 * This prop DEFAULTS TO TRUE, so leaving it off meant that mounting this wrapper at launch
			 * silently stole the default camera back from the preview's orbit camera — OrbitControls
			 * rebuilt itself around a camera whose matrix is overwritten from `rawPose` every frame, and
			 * every drag and button did nothing at all.
			 *
			 * Tracking is unaffected either way: it runs off the pipeline in `updateFrame`, not off
			 * whichever camera three is rendering with.
			 */
			makeDefault={!(Jute.isPreviewSimulator && activeTrackingType === ITrackingTypes.world)}
			poseMode="anchor-origin"
			// poseMode="attitude"
			// poseMode={Jute.isAppClip && activeTrackingType === ITrackingTypes.world ? "anchor-origin" : "attitude"}
			onFirstFrame={onFirstFrame}
			userFacing={isUserFacing}
			sources={sources}
			permissionRequest={!cameraSource}
			rearCameraMirrorMode={rearCameraMirrorMode}
			// From state, not from `Jute.isCameraSourcePending` alone: that is a plain mutable property
			// read at render time, and the only thing that re-renders this component is the source
			// arriving. Deriving it from the source makes starting reactive by construction.
			start={!!cameraSource || !Jute.isCameraSourcePending}
		/>
	);
};

ZapparCameraWrapper.displayName = 'ZapparCameraWrapper';
export default React.memo(ZapparCameraWrapper);
